import { addMonths, endOfDay, startOfDay, startOfISOWeek, startOfMonth } from 'date-fns'
import { FC, MouseEvent, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { InfoOutlined } from '@mui/icons-material'
import { Box, Divider, List, ListItemButton, Paper, Popover, Typography } from '@mui/material'

import { useInternalAccessCheck } from '../../features/account/hooks/roleHooks'
import { useCurrentUserLanguage } from '../../features/account/hooks/useCurrentUserLanguage'
import { LiveEventInfoTooltipContent } from '../../features/live-events/components/LiveEventsCalendar/LiveEventsCalendarItem/LiveEventsCalendarTrackingEventItem/LiveEventInfoTooltipContent'
import { EventDialogTab } from '../../features/live-events/hooks/useEventDialogTabs'
import { useLiveEventModal } from '../../features/live-events/hooks/useLiveEventModal'
import { TrackingEventTimelineItem } from '../../features/live-events/types/LiveEvents'
import { htmlDecode } from '../../helpers/dom'
import { DateRangeValue } from '../DateRangePicker/DateRangePicker'
import GRCircularProgress from '../GRCircularProgress/GRCircularProgress'
import { GRTooltip } from '../GRTooltip/GRTooltip'

const defaultBgColor = '#273143'
const WEEKLY_BUCKET_THRESHOLD_MONTHS = 6
const MONTHLY_BUCKET_THRESHOLD_MONTHS = 12

type BucketTier = 'day' | 'week' | 'month'

type EventGroup = {
  bucketTime: number
  events: TrackingEventTimelineItem[]
  // When set, the marker is positioned here instead of by tier/bucketTime — used to pin an edge
  // group that absorbed out-of-scale events to the scale boundary so it doesn't drift inward.
  pinTime?: number
}

const getBucketStart = (timestamp: number, tier: BucketTier): number => {
  const date = new Date(timestamp)
  switch (tier) {
    case 'month':
      return startOfMonth(date).getTime()
    case 'week':
      return startOfISOWeek(date).getTime()
    default:
      return startOfDay(date).getTime()
  }
}

// Bucketing widens with the selected range: daily for short ranges, weekly past ~6 months,
// monthly past ~1 year — so markers stay readable as the range grows.
const getBucketTier = (fromDate: Date, toTime: number): BucketTier => {
  // Compare against endOfDay so the boundary is a strict "> N months": an exactly-N-month range
  // stays in the lower tier (e.g. exactly 6 months → daily, exactly 12 → weekly).
  if (toTime > endOfDay(addMonths(fromDate, MONTHLY_BUCKET_THRESHOLD_MONTHS)).getTime()) return 'month'
  if (toTime > endOfDay(addMonths(fromDate, WEEKLY_BUCKET_THRESHOLD_MONTHS)).getTime()) return 'week'
  return 'day'
}

// Markers shrink as the range widens so dense ranges stay readable; the everyday short range
// keeps the larger, easily-clickable size.
const MARKER_STYLE_BY_TIER: Record<BucketTier, { size: number; fontSize: number }> = {
  day: { size: 22, fontSize: 10 },
  week: { size: 18, fontSize: 9 },
  month: { size: 14, fontSize: 8 },
}

type NewEventsTimelineStripProps = {
  events: TrackingEventTimelineItem[]
  dateRange?: DateRangeValue
  chartAreaLeft?: number
  chartAreaRight?: number
  containerWidth?: number
  xScaleMin?: number
  xScaleMax?: number
  isLoading?: boolean
}

export const NewEventsTimelineStrip: FC<NewEventsTimelineStripProps> = ({
  events,
  dateRange,
  chartAreaLeft = 0,
  chartAreaRight = 0,
  containerWidth = 0,
  xScaleMin,
  xScaleMax,
  isLoading = false,
}) => {
  const [listAnchorEl, setListAnchorEl] = useState<HTMLElement | null>(null)
  const [activeGroup, setActiveGroup] = useState<EventGroup | null>(null)
  const [detailAnchorEl, setDetailAnchorEl] = useState<HTMLElement | null>(null)
  const [activeDetailEvent, setActiveDetailEvent] = useState<TrackingEventTimelineItem | null>(null)
  const [detailFromList, setDetailFromList] = useState(false)
  const [detailOpenLeft, setDetailOpenLeft] = useState(false)
  const { t } = useTranslation()
  const userLanguage = useCurrentUserLanguage()
  const internalUser = useInternalAccessCheck()
  const { showModal: showEventModal } = useLiveEventModal()

  const getDescriptionPreview = (event: TrackingEventTimelineItem) => {
    const comment = event.trackingEvent.comment?.content?.comment
    const descriptionHTML = comment?.[userLanguage] ? comment[userLanguage] : comment?.['en']
    if (!descriptionHTML) return null
    const paragraphs = descriptionHTML.split('</p>')
    const paragraph = paragraphs.find((p) => !!p.replace(/<\/?[^>]+(>|$)/g, ''))
    const strippedParagraph = paragraph?.replace(/<\/?[^>]+(>|$)/g, '') || ''
    const decoded = htmlDecode(strippedParagraph)
    if (!decoded) return null
    return decoded.length > 300 ? decoded.substring(0, 300) + '...' : decoded
  }

  const handleGroupClick = (e: MouseEvent<HTMLElement>, group: EventGroup) => {
    setListAnchorEl(e.currentTarget)
    setActiveGroup(group)
    setDetailAnchorEl(null)
    setActiveDetailEvent(null)
  }

  const handleRowClick = (e: MouseEvent<HTMLElement>, event: TrackingEventTimelineItem) => {
    const rect = e.currentTarget.getBoundingClientRect()
    setDetailOpenLeft(rect.right + 420 > window.innerWidth)
    setDetailAnchorEl(e.currentTarget)
    setActiveDetailEvent(event)
    setDetailFromList(true)
  }

  const handleListClose = () => {
    setListAnchorEl(null)
    setDetailAnchorEl(null)
  }

  const handleDetailClose = () => {
    setDetailAnchorEl(null)
  }

  const eventGroupsData = useMemo(() => {
    if (!dateRange?.fromDate || !dateRange?.toDate) return null

    const fromDate = dateRange.fromDate
    const fromTime = fromDate.getTime()
    const toTime = endOfDay(dateRange.toDate).getTime()

    const visibleEvents = events.filter((e) => e.trackingEvent.start >= fromTime && e.trackingEvent.start <= toTime)
    if (visibleEvents.length === 0) {
      return { labelOnly: true as const, groups: null, scaleMin: 0, scaleMax: 0 }
    }

    const bucketTier = getBucketTier(fromDate, toTime)

    const groupMap = new Map<number, TrackingEventTimelineItem[]>()
    for (const event of visibleEvents) {
      const bucket = getBucketStart(event.trackingEvent.start, bucketTier)
      if (!groupMap.has(bucket)) groupMap.set(bucket, [])
      groupMap.get(bucket)!.push(event)
    }

    const allGroups: EventGroup[] = Array.from(groupMap.entries())
      .map(([bucketTime, evts]) => ({ bucketTime, events: [...evts].sort((a, b) => a.trackingEvent.start - b.trackingEvent.start) }))
      .sort((a, b) => a.bucketTime - b.bucketTime)

    const scaleMin = xScaleMin ?? fromTime
    const scaleMax = xScaleMax ?? toTime
    if (scaleMax === scaleMin) return null

    const beforeScale = allGroups.filter((g) => g.bucketTime < scaleMin)
    const withinScale = allGroups.filter((g) => g.bucketTime >= scaleMin && g.bucketTime <= scaleMax)
    const afterScale = allGroups.filter((g) => g.bucketTime > scaleMax)

    let groups: EventGroup[]
    if (withinScale.length > 0) {
      // Merge before/after-scale events into the edge within-scale groups
      groups = [...withinScale]
      if (beforeScale.length > 0) {
        const first = groups[0]
        groups[0] = {
          ...first,
          // Pin to the left edge: this group now also represents events from before the visible
          // scale, so its marker should sit at scaleMin rather than drift to its in-scale bucket.
          pinTime: scaleMin,
          events: [...beforeScale.flatMap((g) => g.events), ...first.events].sort((a, b) => a.trackingEvent.start - b.trackingEvent.start),
        }
      }
      if (afterScale.length > 0) {
        const last = groups[groups.length - 1]
        groups[groups.length - 1] = {
          ...last,
          events: [...last.events, ...afterScale.flatMap((g) => g.events)].sort((a, b) => a.trackingEvent.start - b.trackingEvent.start),
        }
      }
    } else {
      groups = []
      if (beforeScale.length > 0) {
        groups.push({
          bucketTime: scaleMin,
          events: beforeScale.flatMap((g) => g.events).sort((a, b) => a.trackingEvent.start - b.trackingEvent.start),
        })
      }
      if (afterScale.length > 0) {
        groups.push({
          bucketTime: scaleMax,
          events: afterScale.flatMap((g) => g.events).sort((a, b) => a.trackingEvent.start - b.trackingEvent.start),
        })
      }
    }

    return { labelOnly: false as const, groups, scaleMin, scaleMax, bucketTier }
  }, [events, dateRange, xScaleMin, xScaleMax])

  const labelWithTooltip = (
    <GRTooltip content={t('live-events:new_events_timeline_strip_info')}>
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          width: chartAreaLeft > 0 ? chartAreaLeft - 8 : undefined,
          top: '50%',
          transform: 'translateY(-50%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          cursor: 'default',
          pointerEvents: 'auto',
        }}
      >
        <Typography variant="caption" color="primary.main" fontWeight="bold" textAlign="center" lineHeight={1.2}>
          {t('live-events:new_events_timeline_strip_label')}
        </Typography>
        <InfoOutlined sx={{ fontSize: 12, color: 'primary.main', flexShrink: 0 }} />
      </Box>
    </GRTooltip>
  )

  const paddingRight = containerWidth > 0 ? containerWidth - chartAreaRight : 0

  if (isLoading) {
    return (
      <Box sx={{ position: 'relative', height: 50 }}>
        {labelWithTooltip}
        <Box
          sx={{
            position: 'absolute',
            left: chartAreaLeft,
            right: paddingRight,
            top: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <GRCircularProgress circularProgressProps={{ size: 20 }} />
        </Box>
      </Box>
    )
  }

  if (!eventGroupsData) return null

  if (eventGroupsData.labelOnly) return <Box sx={{ position: 'relative', height: 50 }}>{labelWithTooltip}</Box>

  const { groups, scaleMin, scaleMax, bucketTier } = eventGroupsData
  const detailBgColor = activeDetailEvent?.tagGroup?.colorHex ?? defaultBgColor
  const { size: markerSize, fontSize: markerFontSize } = MARKER_STYLE_BY_TIER[bucketTier]
  const markerRadius = markerSize / 2

  return (
    <>
      <Box sx={{ position: 'relative', height: 50 }}>
        {labelWithTooltip}
        <Box
          sx={{
            position: 'absolute',
            left: chartAreaLeft,
            right: paddingRight,
            top: 0,
            bottom: 0,
          }}
        >
          {groups.map((group) => {
            const scaleRange = scaleMax - scaleMin
            const positionTime = group.pinTime ?? (bucketTier === 'month' ? group.bucketTime : group.events[0].trackingEvent.start)
            const leftPercent = scaleRange > 0 ? ((positionTime - scaleMin) / scaleRange) * 100 : 0
            const count = group.events.length

            return (
              <Box
                key={group.bucketTime}
                onClick={(e) => handleGroupClick(e, group)}
                sx={{
                  position: 'absolute',
                  left: `clamp(${markerRadius}px, ${leftPercent}%, calc(100% - ${markerRadius}px))`,
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: markerSize,
                  height: markerSize,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  pointerEvents: 'auto',
                  transition: 'transform 0.15s ease',
                  '&:hover': { transform: 'translate(-50%, -50%) scale(1.2)' },
                }}
              >
                <Typography sx={{ fontSize: `${markerFontSize}px`, fontWeight: 'bold', color: 'white', lineHeight: 1 }}>{count}</Typography>
              </Box>
            )
          })}
        </Box>
      </Box>

      {/* List popover for grouped markers */}
      <Popover
        open={!!listAnchorEl}
        anchorEl={listAnchorEl}
        onClose={handleListClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{ paper: { sx: { mt: -1 } } }}
        TransitionProps={{ onExited: () => setActiveGroup(null) }}
      >
        <List disablePadding sx={{ minWidth: 220, maxWidth: 320, maxHeight: 400, overflowY: 'auto' }}>
          {activeGroup?.events.map((event, i) => (
            <Box key={event.trackingEventId}>
              {i > 0 && <Divider />}
              <ListItemButton sx={{ py: 1, px: 2 }} onClick={(e) => handleRowClick(e, event)}>
                <Box>
                  <Typography variant="body2" fontWeight="bold" sx={{ lineHeight: 1.3 }}>
                    {event.title}
                  </Typography>
                  {event.tagGroup && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: event.tagGroup.colorHex, flexShrink: 0 }} />
                      <Typography variant="caption" color="text.secondary">
                        {event.tagGroup.name}
                      </Typography>
                    </Box>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {new Date(event.trackingEvent.start).toLocaleDateString(userLanguage, { month: 'short', day: 'numeric', year: 'numeric' })}
                  </Typography>
                </Box>
              </ListItemButton>
            </Box>
          ))}
        </List>
      </Popover>

      {/* Detail popover for single marker click or list row click */}
      <Popover
        open={!!detailAnchorEl}
        anchorEl={detailAnchorEl}
        onClose={handleDetailClose}
        anchorOrigin={detailFromList ? { vertical: 'bottom', horizontal: detailOpenLeft ? 'left' : 'right' } : { vertical: 'top', horizontal: 'center' }}
        transformOrigin={detailFromList ? { vertical: 'bottom', horizontal: detailOpenLeft ? 'right' : 'left' } : { vertical: 'bottom', horizontal: 'center' }}
        TransitionProps={{ onExited: () => setActiveDetailEvent(null) }}
        slotProps={{
          paper: {
            sx: {
              p: 0,
              overflow: 'auto',
              border: `2px solid ${detailBgColor}`,
              borderRadius: '20px',
              ml: detailFromList && !detailOpenLeft ? '24px' : undefined,
              mr: detailFromList && detailOpenLeft ? '24px' : undefined,
            },
          },
        }}
      >
        <Paper
          elevation={8}
          sx={{
            p: 2,
            maxWidth: 600,
            maxHeight: 500,
            overflowY: 'auto',
            borderRadius: '18px',
          }}
        >
          {activeDetailEvent && (
            <LiveEventInfoTooltipContent
              item={{ ...activeDetailEvent, performanceChanges: undefined }}
              itemContext={{ title: activeDetailEvent.title }}
              bgColor={detailBgColor}
              newItem={activeDetailEvent.new}
              firstInstance={false}
              mechanicChange={activeDetailEvent.firstMechanicChange}
              inactive={false}
              descriptionRawHtml={
                activeDetailEvent.trackingEvent.active === false && !internalUser
                  ? `<em>${t('live-events:event_wip_tooltip')}</em>`
                  : getDescriptionPreview(activeDetailEvent)
              }
              onViewDetails={() => {
                showEventModal({
                  trackedGameId: activeDetailEvent.trackedGame.game.id,
                  eventTypeId: activeDetailEvent.trackingEvent.typeId,
                  eventId: activeDetailEvent.trackingEvent.eventId,
                  durationStart: activeDetailEvent.trackingEvent.start,
                  durationEnd: activeDetailEvent.trackingEvent.end,
                  tab: EventDialogTab.Description,
                })
                // Leave the list + detail popovers open so closing the event modal
                // returns the user to the tooltip/list they launched it from.
              }}
            />
          )}
        </Paper>
      </Popover>
    </>
  )
}
